import React, { Dispatch, SetStateAction } from 'react';
import { Label } from '@/components/ui/label';
import type { FundraisingFormData } from '../../drawers/types/FundraisingPaymentTypes';

// Props interface for the DedicationInformation component
interface DedicationInformationProps {
  formData: FundraisingFormData;
  setFormData: Dispatch<SetStateAction<FundraisingFormData>>;
  handleFieldFocus: (fieldName: string) => void;
  invalidFields: Set<string>;
  dedicationTypes: string[];
  enableDedicationMessage: boolean;
}

export default function DedicationInformation({
  formData,
  setFormData,
  handleFieldFocus,
  invalidFields,
  dedicationTypes,
  enableDedicationMessage,
}: DedicationInformationProps): React.ReactElement {
  // Helper function to safely check if a field is invalid
  const isFieldInvalid = (fieldName: string): boolean => {
    if (!invalidFields) return false;
    if (invalidFields instanceof Set) {
      return invalidFields.has(fieldName);
    }
    return false;
  };

  return (
    <div>
      <div className="mt-2 space-y-3">
        {/* Dedication Type */}
        <div className="space-y-0">
          <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
            Dedication Type
            <span className="ml-1 text-ink-muted">(optional)</span>
          </Label>
          <select
            value={formData.dedicationType || ''}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                dedicationType: e.target.value,
              }))
            }
            className="block h-7 w-full shrink-0 rounded-md border border-edge bg-white px-2 py-0 text-sm leading-tight"
          >
            <option value="">Select dedication type</option>
            {dedicationTypes.map((type, idx) => (
              <option key={idx} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>

        {/* Dedication Name - Required when type is selected */}
        {formData.dedicationType && (
          <div className="space-y-0">
            <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
              {formData.dedicationType}
              <span className="ml-1 text-red-500">*</span>
            </Label>
            <input
              type="text"
              value={formData.dedicationName || ''}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  dedicationName: e.target.value,
                }))
              }
              placeholder="Enter name"
              required
              className={`block h-7 w-full shrink-0 rounded-md border px-2 py-0 text-sm leading-tight ${
                isFieldInvalid('dedication_information-dedicationName')
                  ? 'border-red-500'
                  : 'border-edge bg-white'
              }`}
              onFocus={() => handleFieldFocus('dedication_information-dedicationName')}
            />
          </div>
        )}

        {/* Dedication Message - Show only if enabled and type is selected */}
        {enableDedicationMessage && formData.dedicationType && (
          <div className="space-y-0">
            <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
              Dedication Message
              <span className="ml-1 text-ink-muted">(optional)</span>
            </Label>
            <textarea
              value={formData.dedicationMessage || ''}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  dedicationMessage: e.target.value,
                }))
              }
              placeholder="Enter a personal message"
              rows={3}
              maxLength={500}
              className="block w-full shrink-0 rounded-md border border-edge bg-white px-2 py-1 text-sm leading-tight"
            />
            <p className="text-xs text-ink-muted text-right">
              {(formData.dedicationMessage || '').length}/500 characters
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
